import React, { useState, useEffect, useContext } from "react";
import {
  Building2,
  Users,
  Trash2,
  CheckCircle,
  XCircle,
  RefreshCw,
  Edit,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import API from "../../api/axios";
import { AuthContext } from "../../context/AuthContext";

// Import unified PaginationSearchbar component
import PaginationSearchbar from "../../GlobalComponents/PaginationSearchbar";

// Import central theme classes from MasterCssClass.js
import {
  masterPanelCard,
  masterHeadingMain,
  masterTableWrap,
  masterTable,
  masterTableHead,
  masterTableRow,
  masterBtnBack,
  masterPageContainer,
  masterHeaderContent,
  masterHeaderBrand,
  masterHeaderIconBox,
  masterHeaderActions,
  masterTableContainer,
  masterThCode,
  masterThName,
  masterThStatus,
  masterThManage,
  masterTdCode,
  masterTdName,
  masterTdStatus,
  masterTdManage,
  masterTdManageFlex,
  masterTdEmpty,
} from "../../GlobalComponents/MasterCssClass";

const AVAILABLE_PERMISSIONS = [
  { key: "exam-master", label: "Exam Master" },
  { key: "category-master", label: "Category Master" },
  { key: "syllabus-master", label: "Syllabus Master" },
  { key: "question-bank", label: "Question Bank" },
  { key: "pyq-master", label: "PYQ" },
  { key: "study-material-master", label: "Study Material" },
  { key: "mock-test", label: "Mock Test Set" },
  { key: "video-master", label: "Video Master" },
  { key: "student-approvals", label: "Student Approvals" },
  { key: "referral-master", label: "Referral Master" },
  { key: "faculty-master", label: "Faculty" },
];

const AdminOrgApprovals = () => {
  const { user: currentUser } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState("organizations"); // "organizations" or "students"
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- CONTROL STATES ---
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // --- EDIT PERMISSIONS MODAL STATE ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgPermissions, setOrgPermissions] = useState([]);

  // FETCH RECORDS BASED ON ACTIVE TAB
  const fetchRecords = async () => {
    setLoading(true);
    try {
      const endpoint =
        activeTab === "organizations"
          ? "/auth/admin/organization-requests"
          : "/auth/admin/student-requests";
      const res = await API.get(endpoint);
      setDataList(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch {
      toast.error(`FAILED TO LOAD ${activeTab.toUpperCase()} RECORDS`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
    setCurrentPage(1);
    setSearchTerm("");
  }, [activeTab]);

  // UPDATE STATUS (ACCEPT / REJECT)
  const handleStatusChange = async (id, status) => {
    try {
      const endpoint =
        activeTab === "organizations"
          ? `/auth/admin/organization-status/${id}`
          : `/auth/admin/student-status/${id}`;
      await API.patch(endpoint, { status });
      toast.success(`RECORD ${status}`);
      fetchRecords();
    } catch (err) {
      toast.error(err.response?.data?.message || "ACTION FAILED");
    }
  };

  // OPEN PERMISSION MODAL (Organizations only)
  const handleOpenPermissionsModal = (org) => {
    if (org.email?.toLowerCase() === "medhashala@gmail.com") return;
    setSelectedOrg(org);
    setOrgPermissions(Array.isArray(org.permissions) ? org.permissions : []);
    setIsModalOpen(true);
  };

  // TOGGLE INDIVIDUAL PERMISSION CHECKBOX
  const handleCheckboxToggle = (permKey) => {
    setOrgPermissions((prev) =>
      prev.includes(permKey)
        ? prev.filter((p) => p !== permKey)
        : [...prev, permKey]
    );
  };

  // SAVE PERMISSIONS TO BACKEND
  const handleSavePermissions = async () => {
    if (!selectedOrg) return;
    try {
      await API.patch(`/auth/admin/organization-permissions/${selectedOrg._id}`, {
        permissions: orgPermissions,
      });
      toast.success("PERMISSIONS UPDATED SUCCESSFULLY");
      setIsModalOpen(false);
      fetchRecords();
    } catch (err) {
      toast.error(err.response?.data?.message || "FAILED TO UPDATE PERMISSIONS");
    }
  };

  // DELETE RECORD
  const handleDelete = async (id, email) => {
    if (email?.toLowerCase() === "medhashala@gmail.com") {
      toast.error("MAIN ADMIN ACCOUNT CANNOT BE DELETED");
      return;
    }
    if (window.confirm("ARE YOU SURE YOU WANT TO DELETE THIS RECORD?")) {
      try {
        const endpoint =
          activeTab === "organizations"
            ? `/auth/admin/organization/${id}`
            : `/auth/admin/student/${id}`;
        await API.delete(endpoint);
        toast.success("DELETED SUCCESSFULLY");
        fetchRecords();
      } catch {
        toast.error("DELETE FAILED");
      }
    }
  };

  // --- FILTERING ---
  const filteredData = dataList.filter((item) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();

    const startsWithFirstLetter = (value) => {
      if (!value) return false;
      return value.trim().toLowerCase().startsWith(term);
    };

    return (
      startsWithFirstLetter(item.name) ||
      startsWithFirstLetter(item.userId) ||
      startsWithFirstLetter(item.tenantKey) ||
      startsWithFirstLetter(item.email) ||
      startsWithFirstLetter(item.referralCode) ||
      startsWithFirstLetter(item.adminApprovalStatus)
    );
  });

  // --- PAGINATION SLICING ---
  const isAll = rowsPerPage === "ALL" || rowsPerPage === -1;
  const startIndex = isAll ? 0 : (currentPage - 1) * rowsPerPage;
  const paginatedData = isAll
    ? filteredData
    : filteredData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <div className={`${masterPageContainer} h-full flex flex-col overflow-y-auto custom-scrollbar relative`}>
      {/* HEADER CARD */}
      <div className={masterPanelCard}>
        <div className={masterHeaderContent}>
          <div className={masterHeaderBrand}>
            <div className={masterHeaderIconBox}>
              {activeTab === "organizations" ? <Building2 size={13} /> : <Users size={13} />}
            </div>
            <h1 className={masterHeadingMain}>
              {activeTab === "organizations"
                ? "Organization Access Requests & Permissions"
                : "Student Registration Approvals"}
            </h1>
          </div>

          <div className={masterHeaderActions}>
        
            {/* <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
              <button
                type="button"
                onClick={() => setActiveTab("organizations")}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                  activeTab === "organizations"
                    ? "bg-[#0E3652] text-white shadow"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Organizations
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("students")}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                  activeTab === "students"
                    ? "bg-[#0E3652] text-white shadow"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Students
              </button>
            </div> */}

            <button
              type="button"
              onClick={fetchRecords}
              className={masterBtnBack}
              title="Refresh Records"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              <span>SYNC</span>
            </button>
          </div>
        </div>
      </div>

      {/* COMBINED SEARCH & PAGINATION BAR */}
      <PaginationSearchbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="SEARCH NAME, ID, TENANT, EMAIL, OR STATUS BY FIRST LETTER..."
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={setRowsPerPage}
        totalItems={filteredData.length}
      />

      {/* TABLE */}
      <div className={`${masterTableContainer} flex-1 min-h-0 flex flex-col`}>
        <div className={`${masterTableWrap} overflow-x-auto overflow-y-auto custom-scrollbar flex-1 max-h-[calc(100vh-260px)]`}>
          <table className={masterTable}>
            <thead>
              <tr className={masterTableHead}>
                <th className={masterThCode}>{activeTab === "organizations" ? "ORG ID" : "STUDENT ID"}</th>
                <th className={masterThName}>{activeTab === "organizations" ? "INSTITUTION NAME" : "STUDENT NAME"}</th>
                <th className={masterThName}>TENANT KEY</th>
                <th className={masterThName}>EMAIL</th>
                <th className={masterThName}>MOBILE NO.</th>
                <th className={masterThStatus}>STATUS</th>
                <th className={masterThManage}>MANAGE</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className={masterTdEmpty}>
                    Syncing Database...
                  </td>
                </tr>
              ) : paginatedData.length > 0 ? (
                paginatedData.map((item) => {
                  const isApproved = item.adminApprovalStatus === "APPROVED";
                  const isRejected = item.adminApprovalStatus === "REJECTED";
                  const isMainAdminAccount = item.email?.toLowerCase() === "medhashala@gmail.com";

                  return (
                    <tr key={item._id} className={masterTableRow}>
                      <td className={masterTdCode}>{item.userId || item.studentId || "N/A"}</td>
                      <td className={masterTdName}>
                        <div className="font-semibold">{item.name}</div>
                      </td>
                      <td className={masterTdName}>
                        <span>{item.tenantKey || "N/A"}</span>
                      </td>
                      <td className={masterTdName}>
                        <div>{item.email}</div>
                      </td>
                      <td className={masterTdName}>
                        <div>{item.phone || "N/A"}</div>
                      </td>
                     
                      <td className={masterTdStatus}>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold ${
                            isApproved
                              ? "bg-emerald-100 text-emerald-800"
                              : isRejected
                              ? "bg-red-100 text-red-800"
                              : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {item.adminApprovalStatus || "APPROVED"}
                        </span>
                      </td>
                      <td className={masterTdManage}>
                        <div className={masterTdManageFlex}>
                          {isMainAdminAccount ? (
                            <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded">
                              Main Admin (Locked)
                            </span>
                          ) : (
                            <>
                              {/* Permissions Edit Button (Organizations only) */}
                              {activeTab === "organizations" && (
                                <button
                                  type="button"
                                  onClick={() => handleOpenPermissionsModal(item)}
                                  className="flex items-center gap-1 px-2.5 py-1 bg-indigo-600 text-white hover:bg-indigo-700 rounded text-[11px] font-semibold shadow-sm transition-all"
                                  title="Configure Permissions"
                                >
                                  <Edit size={12} /> Perms
                                </button>
                              )}

                              {/* Accept Button */}
                              <button
                                type="button"
                                onClick={() => handleStatusChange(item._id, "APPROVED")}
                                disabled={isApproved}
                                className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold transition-all ${
                                  isApproved
                                    ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                                    : "bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm"
                                }`}
                                title="Accept"
                              >
                                <CheckCircle size={12} /> Accept
                              </button>

                              {/* Reject Button */}
                              <button
                                type="button"
                                onClick={() => handleStatusChange(item._id, "REJECTED")}
                                disabled={isRejected}
                                className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold transition-all ${
                                  isRejected
                                    ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                                    : "bg-amber-600 text-white hover:bg-amber-700 shadow-sm"
                                }`}
                                title="Reject"
                              >
                                <XCircle size={12} /> Reject
                              </button>

                              {/* Delete Button */}
                              <button
                                type="button"
                                onClick={() => handleDelete(item._id, item.email)}
                                className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                                title="Delete"
                              >
                                <Trash2 size={13} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className={masterTdEmpty}>
                    No Records Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* COMPACT PERMISSIONS CONFIGURATION MODAL */}
      {isModalOpen && selectedOrg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-3">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden border border-slate-200 animate-in fade-in zoom-in duration-150">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-[#0E3652] text-white">
              <div>
                <h3 className="font-bold text-sm">Control Panel Permissions</h3>
                <p className="text-[11px] text-slate-300 truncate max-w-[260px]">{selectedOrg.name}</p>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="text-slate-300 hover:text-white p-1 rounded transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Body / Compact 2-Column Grid Checkboxes */}
            <div className="p-4 space-y-2">
              <p className="text-[11px] font-medium text-slate-500 mb-2">
                Select permitted admin sections:
              </p>
              <div className="grid grid-cols-2 gap-2">
                {AVAILABLE_PERMISSIONS.map((perm) => {
                  const isChecked = orgPermissions.includes(perm.key);
                  return (
                    <label
                      key={perm.key}
                      className={`flex items-center justify-between px-2.5 py-2 rounded border cursor-pointer transition-all ${
                        isChecked ? "border-[#0E3652] bg-blue-50/60" : "border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      <span className="text-[11px] font-semibold text-slate-700 truncate pr-1">{perm.label}</span>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleCheckboxToggle(perm.key)}
                        className="w-3.5 h-3.5 text-[#0E3652] rounded border-slate-300 focus:ring-[#0E3652] shrink-0"
                      />
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-2 px-4 py-2.5 bg-slate-50 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-3 py-1.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-200 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSavePermissions}
                className="px-4 py-1.5 text-[11px] font-semibold bg-[#0E3652] text-white hover:bg-[#1a4f75] rounded shadow-sm transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrgApprovals;